import { Donor } from '@/types';

interface CachedDonorData {
  donors: Donor[];
  lastUpdated: number;
  version: string;
}

const CACHE_KEY = 'donors_cache';
const CACHE_VERSION = '1.0.0';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 ore

class DonorCacheService {
  private cache: CachedDonorData | null = null;

  /**
   * Carica i donatori dalla cache locale
   */
  async getCachedDonors(): Promise<Donor[]> {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) {
        console.log('📋 No cached donors found');
        return [];
      }

      const data: CachedDonorData = JSON.parse(cached);
      
      // Controlla se la cache è scaduta
      if (this.isCacheExpired(data.lastUpdated)) {
        console.log('⏰ Cached donors expired');
        this.clearCache();
        return [];
      }

      console.log(`📋 Loaded ${data.donors.length} donors from cache`);
      this.cache = data;
      return data.donors;
    } catch (error) {
      console.error('❌ Error loading cached donors:', error);
      this.clearCache();
      return [];
    }
  }

  /**
   * Salva i donatori nella cache locale
   */
  async saveDonorsToCache(donors: Donor[]): Promise<void> {
    try {
      const data: CachedDonorData = {
        donors,
        lastUpdated: Date.now(),
        version: CACHE_VERSION,
      };

      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      this.cache = data;
      console.log(`💾 Saved ${donors.length} donors to cache`);
    } catch (error) {
      console.error('❌ Error saving donors to cache:', error);
    }
  }

  /**
   * Controlla se la cache è scaduta
   */
  private isCacheExpired(lastUpdated: number): boolean {
    return Date.now() - lastUpdated > CACHE_TTL;
  }

  /**
   * Pulisce la cache
   */
  clearCache(): void {
    localStorage.removeItem(CACHE_KEY);
    this.cache = null;
    console.log('🗑️ Donors cache cleared');
  }

  /**
   * Controlla se ci sono donatori in cache
   */
  hasCachedDonors(): boolean {
    return this.cache !== null && this.cache.donors.length > 0;
  }

  /**
   * Ottiene il timestamp dell'ultimo aggiornamento
   */
  getLastUpdated(): number | null {
    return this.cache?.lastUpdated || null;
  }

  /**
   * Controlla se la cache è valida e non scaduta
   */
  isCacheValid(): boolean {
    if (!this.cache) return false;
    return !this.isCacheExpired(this.cache.lastUpdated);
  }
}

export const donorCacheService = new DonorCacheService();
